import { Injectable } from '@angular/core';
import {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signOut,
  fetchAuthSession,
  fetchUserAttributes,
  updatePassword,
  resetPassword,
  confirmResetPassword,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignUpInput,
  type ResetPasswordInput,
} from '@aws-amplify/auth';
import { from, Observable, of, forkJoin } from 'rxjs';
import { map, mapTo, switchMap, tap, catchError } from 'rxjs/operators';

import {
  IConfirmSignUpInfoModel,
  IForgotPasswordCodeInitModel,
  IForgotPasswordResetModel,
  ILoginInfoModel,
  IResetPasswordModel,
  ISignUpInfoModel,
  ISignUpResultModel,
  IUserCognitoInfoModel,
} from '@features/auth-amplify/models';
import { mapCognitoAttributes } from '@features/auth-amplify/utils';
import { AuthTokenService } from '../../auth/services/auth-token.service';
import { PendingEmailConfirmationService } from './pending-email-confirmation.service';

@Injectable({ providedIn: 'root' })
export class CognitoService {
  constructor(
    private readonly authTokenService: AuthTokenService,
    private readonly pendingSvc: PendingEmailConfirmationService
  ) {}
  public login = (loginInfo: ILoginInfoModel): Observable<IUserCognitoInfoModel> =>
    from(signIn({ username: loginInfo.email, password: loginInfo.password } as SignInInput)).pipe(
      switchMap(() =>
        forkJoin({
          session: from(fetchAuthSession()).pipe(catchError(() => of(null))),
          attrs: from(fetchUserAttributes()).pipe(catchError(() => of({} as any))),
        })
      ),
      map(({ session, attrs }: any) => ({
        token: session?.tokens?.idToken?.toString() || null,
        attrs: mapCognitoAttributes(attrs || {}),
      })),
      tap(({ token, attrs }: any) => {
        if (attrs?.isEmailVerified) {
          if (token) {
            this.authTokenService.cacheToken(token, { force: true });
          }
          this.pendingSvc.clearPending();
        } else {
          this.authTokenService.clearCachedToken();
          this.pendingSvc.markPending();
        }
      }),
      map(({ attrs }) => attrs)
    );
  public signUp = (signUpInfo: ISignUpInfoModel, tenantId?: string): Observable<ISignUpResultModel> =>
    from(
      signUp({
        username: signUpInfo.email,
        password: signUpInfo.password,
        options: {
          userAttributes: {
            email: signUpInfo.email,
            ['custom:su-firstName']: signUpInfo.firstName,
            ['custom:su-lastName']: signUpInfo.lastName,
            ['custom:su-title']: signUpInfo.title,
            ['custom:su-company']: signUpInfo.company,
            ['custom:su-landline']: signUpInfo.landline,
            ['custom:su-primaryAccount']: signUpInfo.primaryAccount ? String(signUpInfo.primaryAccount) : '',
            ['custom:su-tenantId']: tenantId || '',
          },
        },
      } as SignUpInput)
    ).pipe(
      map((result: any) => {
        const isVerified = !!result.isSignUpComplete || !!result.userConfirmed;
        if (!isVerified) {
          this.pendingSvc.markPending();
          this.authTokenService.clearCachedToken();
        }
        return {
          isInvitationSignUp: !!tenantId,
          user: {
            id: result.userId || result.userSub || result?.user?.userId,
            email: signUpInfo.email,
            isEmailVerified: isVerified,
          },
        };
      })
    );

  public confirmSignUp = (confirmSignUpInfo: IConfirmSignUpInfoModel): Observable<void> =>
    from(
      confirmSignUp({
        username: confirmSignUpInfo.email,
        confirmationCode: confirmSignUpInfo.code,
      } as ConfirmSignUpInput)
    ).pipe(
      tap(() => {
        // force explicit login after confirmation
        this.authTokenService.clearCachedToken();
        this.pendingSvc.clearPending();
      }),
      mapTo(null)
    );

  public resetPassword = (resetPasswordModel: IResetPasswordModel): Observable<void> =>
    from(fetchAuthSession()).pipe(
      switchMap(() =>
        from(
          updatePassword({
            oldPassword: resetPasswordModel.oldPassword,
            newPassword: resetPasswordModel.newPassword,
          })
        )
      ),
      mapTo(null)
    );

  public forgotPasswordInit = (initForgotPassword: IForgotPasswordCodeInitModel): Observable<void> =>
    from(resetPassword({ username: initForgotPassword.email } as ResetPasswordInput)).pipe(mapTo(null));

  public forgotPasswordReset = (resetForgotPassword: IForgotPasswordResetModel): Observable<void> =>
    from(
      confirmResetPassword({
        username: resetForgotPassword.email,
        confirmationCode: resetForgotPassword.code,
        newPassword: resetForgotPassword.password,
      })
    ).pipe(mapTo(null));

  public resendConfirmationCode = (username: string) => from(resendSignUpCode({ username })).pipe(mapTo(null));

  public logout(): Observable<void> {
    // Clear token cache first to avoid any race with parallel requests.
    this.authTokenService.clearCachedToken();
    return from(
      signOut().catch(() => {
        /* swallow signOut error */
      })
    ).pipe(mapTo(undefined));
  }
}
